/**
 * Builder integration states — Loading / Error / Empty screens for BuilderPage.
 * Presentational shell only (no generation, no rendering logic). Friendly, never a
 * raw stack trace, never a crash. See docs/aiWebsiteBuilder/BUILDER_INTEGRATION.md.
 */
import { Loader2, Sparkles, TriangleAlert } from 'lucide-react'
import { BRAND } from '../config'

function Screen({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-[#fafaf8] px-6 text-center">
      {children}
    </div>
  )
}

function Badge() {
  return (
    <div
      className="flex h-14 w-14 items-center justify-center rounded-2xl text-white"
      style={{ background: `linear-gradient(135deg, ${BRAND.red}, ${BRAND.navy})` }}
    >
      <Sparkles className="h-7 w-7" />
    </div>
  )
}

/** Clean full-screen loader shown while a site is being generated. */
export function BuilderLoading({ message }: { message: string }) {
  return (
    <Screen>
      <Badge />
      <Loader2 className="h-6 w-6 animate-spin text-gray-400" aria-hidden="true" />
      <p className="text-sm font-medium text-gray-600" role="status" aria-live="polite">
        {message}
      </p>
    </Screen>
  )
}

/** Friendly builder error screen with Retry / Back actions. */
export function BuilderError({
  message,
  onRetry,
  onBack,
  retryLabel,
  backLabel,
}: {
  message: string
  onRetry?: () => void
  onBack: () => void
  retryLabel: string
  backLabel: string
}) {
  return (
    <Screen>
      <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-red-50 text-red-500">
        <TriangleAlert className="h-7 w-7" />
      </div>
      <h1 className="text-xl font-bold text-gray-900">{message}</h1>
      <div className="mt-2 flex gap-3">
        {onRetry ? (
          <button
            type="button"
            onClick={onRetry}
            className="rounded-lg bg-gray-900 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-gray-700"
          >
            {retryLabel}
          </button>
        ) : null}
        <button
          type="button"
          onClick={onBack}
          className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50"
        >
          {backLabel}
        </button>
      </div>
    </Screen>
  )
}

/** Empty builder state (no schema yet) with a Generate action. */
export function BuilderEmpty({
  title,
  subtitle,
  actionLabel,
  onGenerate,
}: {
  title: string
  subtitle: string
  actionLabel: string
  onGenerate: () => void
}) {
  return (
    <Screen>
      <Badge />
      <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      <p className="max-w-md text-sm text-gray-500">{subtitle}</p>
      <button
        type="button"
        onClick={onGenerate}
        className="mt-2 inline-flex items-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
        style={{ background: `linear-gradient(135deg, ${BRAND.red}, ${BRAND.navy})` }}
      >
        <Sparkles className="h-4 w-4" />
        {actionLabel}
      </button>
    </Screen>
  )
}
